import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  AppBar, Toolbar, Typography, Container, Button,
  Box, Chip, Divider, IconButton, Menu, MenuItem, CircularProgress,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExpedientesList from './components/ExpedientesList';
import ExpedienteDetail from './components/ExpedienteDetail';
import Dashboard from './components/Dashboard';
import ClientesPage from './pages/clientes/ClientesPage';
import TecnicosPage from './pages/tecnicos/TecnicosPage';
import DocumentosPage from './pages/documentos/DocumentosPage';
import ConfiguracionGeneral from './pages/configuracion/ConfiguracionGeneral';
import GenerarDocumento from './pages/GenerarDocumento';
import LoginPage from './pages/LoginPage';
import MinutasPage from './pages/minutas/MinutasPage';
import MinutaDetail from './pages/minutas/MinutaDetail';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

function NavBar() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setAnchorEl(null);
  };

  if (!user) return null;

  return (
    <AppBar position="static">
      <Toolbar>
        <Typography
          variant="h6"
          component={Link}
          to="/"
          sx={{ flexGrow: 1, color: 'inherit', textDecoration: 'none' }}
        >
          Sistema de Peritaciones
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
          {isAdmin && (
            <>
              <Button color="inherit" component={Link} to="/expedientes" size="small">
                Expedientes
              </Button>
              <Button color="inherit" component={Link} to="/clientes" size="small">
                Clientes
              </Button>
              <Button color="inherit" component={Link} to="/tecnicos" size="small">
                Técnicos
              </Button>
              <Button color="inherit" component={Link} to="/documentos" size="small">
                Documentos
              </Button>
            </>
          )}
          <Button color="inherit" component={Link} to="/minutas" size="small">
            Minutas
          </Button>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.3)' }} />

        <Chip
          label={user.rol === 'admin' ? 'Admin' : 'Abogado'}
          size="small"
          color={user.rol === 'admin' ? 'secondary' : 'default'}
          sx={{ mr: 1, color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
          variant="outlined"
        />

        <IconButton color="inherit" onClick={e => setAnchorEl(e.currentTarget)}>
          <AccountCircleIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem disabled>
            <Typography variant="body2">{user.display_name || user.email}</Typography>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>Cerrar sesión</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  // Mientras se lee el localStorage, mostrar spinner centrado
  // Esto evita el flash de "sin navbar" al recargar con sesión guardada
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <NavBar />
      <Routes>
        <Route path="/login" element={user ? <Dashboard /> : <LoginPage />} />

        {/* Minutas: ancho completo de pantalla */}
        <Route path="/minutas" element={
          <ProtectedRoute>
            <Box sx={{ mt: 4, mb: 4, px: 3 }}>
              <MinutasPage />
            </Box>
          </ProtectedRoute>
        } />
        <Route path="/minutas/:id" element={
          <ProtectedRoute>
            <Box sx={{ mt: 4, mb: 4, px: 3 }}>
              <MinutaDetail />
            </Box>
          </ProtectedRoute>
        } />

        {/* Resto de rutas con contenedor limitado */}
        <Route path="/" element={
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <ProtectedRoute><Dashboard /></ProtectedRoute>
          </Container>
        } />
        <Route path="/expedientes" element={
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <ProtectedRoute rol="admin"><ExpedientesList /></ProtectedRoute>
          </Container>
        } />
        <Route path="/expedientes/:id" element={
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <ProtectedRoute rol="admin"><ExpedienteDetail /></ProtectedRoute>
          </Container>
        } />
        <Route path="/clientes/*" element={
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <ProtectedRoute rol="admin"><ClientesPage /></ProtectedRoute>
          </Container>
        } />
        <Route path="/tecnicos" element={
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <ProtectedRoute rol="admin"><TecnicosPage /></ProtectedRoute>
          </Container>
        } />
        <Route path="/documentos" element={
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <ProtectedRoute rol="admin"><DocumentosPage /></ProtectedRoute>
          </Container>
        } />
        <Route path="/configuracion" element={
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <ProtectedRoute rol="admin"><ConfiguracionGeneral /></ProtectedRoute>
          </Container>
        } />
        <Route path="/documentos/generar" element={
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <ProtectedRoute rol="admin"><GenerarDocumento /></ProtectedRoute>
          </Container>
        } />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
